"use client";

import { getParser } from "@/lib/file-parser";
import { supabase } from "@/lib/supabase";
import { useCallback, useState } from "react";

export type UploadedFileRecord = { id: string; name: string };

export function useKnowledgeUpload() {
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = useCallback(
    async (file: File, userId: string): Promise<UploadedFileRecord> => {
      const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : "";
      const storagePath = `${userId}/${crypto.randomUUID()}${ext}`;

      const { error: storageError } = await supabase.storage
        .from("files")
        .upload(storagePath, file, {
          contentType: file.type || "application/octet-stream",
          upsert: false,
        });
      if (storageError) throw new Error(storageError.message);

      const { data: fileRecord, error: fileError } = await supabase
        .from("files")
        .insert({
          user_id: userId,
          file_path: storagePath,
          name: file.name,
          size: file.size,
          tokens: 0,
          type: file.type || "application/octet-stream",
          description: "",
          sharing: "private",
        })
        .select("id, name")
        .single();
      if (fileError || !fileRecord) throw new Error(fileError?.message ?? "Insert failed");

      // Kick off parsing + embedding (fire and forget)
      void supabase.functions.invoke("woods-knowledge-upload", {
        body: {
          fileId: fileRecord.id,
          parser: getParser(file.type, file.name),
          embedding_provider: "voyage",
          embedding_model: "voyage-context-3",
        },
      });

      return fileRecord;
    },
    []
  );

  const uploadFiles = useCallback(
    async (files: File[], userId: string) => {
      setIsUploading(true);
      const succeeded: UploadedFileRecord[] = [];
      const errors: string[] = [];

      for (const file of files) {
        try {
          succeeded.push(await uploadFile(file, userId));
        } catch (err) {
          errors.push(`${file.name}: ${err instanceof Error ? err.message : "Upload failed"}`);
        }
      }

      setIsUploading(false);
      return { succeeded, errors };
    },
    [uploadFile]
  );

  return { isUploading, uploadFile, uploadFiles };
}
